/**
 * Public marketing copy. Claims and prices match the live landing page.
 * No club name, no invented stats, no unattributed quotes.
 */

export type PricingTier = {
  id: 'solo' | 'pro' | 'enterprise';
  name: string;
  tagline: string;
  priceMonthly: number;
  priceYearly: number;
  swimmers: string;
  seats: string;
  pools: string;
  teaser: readonly string[];
  features: readonly string[];
  highlighted?: boolean;
  cta: string;
};

export const hero = {
  titleLead: 'Run the club,',
  titleRest: 'not the spreadsheet.',
  sub: 'Rosters, sessions, meets, and a voice co-pilot for Kiwi swim clubs.',
} as const;

export const copilotIntro = {
  title: 'Built for the coach poolside.',
  sub: 'Talk the way you would to a fellow coach. The co-pilot writes observations, parent emails, and development logs.',
} as const;

export const copilotBands = [
  {
    id: 'voice',
    title: 'Talk. Don’t type.',
    body: 'Tap the mic and say what you saw. Eight seconds. It is transcribed, tagged, and filed on the swimmer’s development log.',
    image: '/marketing/pwa-copilot.webp',
    avif: '/marketing/pwa-copilot.avif',
    alt: 'Co-pilot on a phone for the fictional Harbourside Swim Club, with tap to talk',
    device: 'phone' as const,
    tone: 'tide' as const,
    tilt: 'left' as const,
    companion: null,
    float: null,
  },
  {
    id: 'draft',
    title: 'From observation to draft.',
    body: 'Worth sharing with a parent? The co-pilot drafts the email in the coach’s voice. You review it, edit it, then send when you are ready.',
    image: '/marketing/draft.webp',
    avif: '/marketing/draft.avif',
    alt: 'Parent email draft for the fictional Harbourside Swim Club, ready for the coach to edit and send',
    device: 'macbook' as const,
    tone: 'pool' as const,
    tilt: null,
    companion: null,
    float: null,
  },
  {
    id: 'programs',
    title: 'Tell us the week.',
    body: '“Performance, taper week, 100 IM focus.” Five days of sessions are drafted. You edit them before the lane opens.',
    image: '/marketing/programs.webp',
    avif: '/marketing/programs.avif',
    alt: 'Weekly program deck for the fictional Harbourside Swim Club',
    device: 'tablet' as const,
    tone: 'sunset' as const,
    tilt: 'right' as const,
    companion: null,
    float: null,
  },
  {
    id: 'heads-up',
    title: 'See today in one place.',
    body: 'Today’s deck shows what needs a look, including a session that is nearly full.',
    image: '/marketing/heads-up.webp',
    avif: '/marketing/heads-up.avif',
    alt: 'Club dashboard for the fictional Harbourside Swim Club, with the day’s deck',
    device: 'macbook' as const,
    tone: 'green' as const,
    tilt: null,
    companion: null,
    float: null,
  },
] as const;

/**
 * Roadmap for committees, coaches, and parents. Only work that is not in the
 * product yet, in plain language. `soon` is already started. `later` is planned
 * in the design notes and not in the app yet.
 *
 * When an item ships, remove it from this list and add a plain sentence to
 * `features`. The home page and the features page both read `features`.
 *
 * Backing notes, not for the page: programme sets are started in shared code
 * and the phone still uses plain text. Meet entries and shop orders are still
 * to build (meet calendar notes, shop order notes). Race-day updates, meet
 * suggestions, and the attendance heads-up are in the design plan only.
 */
export const roadmap = {
  title: 'What’s next.',
  note: 'Updated monthly. Built with real clubs.',
  soon: [
    {
      title: 'Programmes with sets',
      body: 'Write the reps, distance, and stroke for each part of the session.',
    },
  ],
  later: [
    {
      title: 'Meet entries prepared for the club',
      body: 'Put the entries together here, ready for the person who submits the meet.',
    },
    {
      title: 'Club shop orders in the app',
      body: 'See what families ordered, alongside the rest of the club.',
    },
    {
      title: 'Live race-day updates',
      body: 'A note on the phone when a swimmer is up, and the time when they finish.',
    },
    {
      title: 'Meet suggestions for each swimmer',
      body: 'Ideas for which events to enter, for the coach to accept or change.',
    },
    {
      title: 'A heads-up when attendance drops',
      body: 'Notice when a swimmer starts missing sessions, before they drift away.',
    },
  ],
} as const;

export const clubIntro = {
  title: 'Everything a club needs.',
  sub: 'Solid tools for how New Zealand clubs work. SNZ member IDs, privacy first, and no lock-in.',
} as const;

export const features = [
  {
    id: 'roster',
    title: 'Member roster',
    body: 'SNZ member IDs, Smart Import from CSV, PDF, or Sheets, Fastlane MS_MEMBER import, and guardian links.',
  },
  {
    id: 'sessions',
    title: 'Squads and sessions',
    body: 'Squads, bookings, and book-in on the phone. Coaches have their own section and can pass a session to another coach. Families get a note when a session is nearly full. The weekly programme is kept on the phone if the app closes.',
  },
  {
    id: 'times',
    title: 'Times and personal bests',
    body: 'SCM and LCM results and personal bests. Enter by hand, or import SNZ .hy3 meet files.',
  },
  {
    id: 'meets',
    title: 'Meets and Fastlane',
    body: 'Meet calendar and results in the app. Deep-link parents to Fastlane. No SNZ API sync yet.',
  },
  {
    id: 'induction',
    title: 'Induction',
    body: 'One intake for a new family. Billing contacts link to Xero and stay as the treasurer set them. WhatsApp and Fastlane steps are still ticked by a person.',
  },
  {
    id: 'comms',
    title: 'Comms',
    body: 'Parent app announcements are live. Email, Facebook, and SMS campaigns are drafted and approved in the app.',
  },
  {
    id: 'store',
    title: 'Club store',
    body: 'Merch catalogue in the parent app. Stripe checkout when it is configured for your club.',
  },
  {
    id: 'site',
    title: 'Branded site',
    body: 'Your colours, logo, blog, events, and squad pages, edited in the admin.',
  },
  {
    id: 'privacy',
    title: 'Privacy',
    body: 'Aligned with the Privacy Act 2020. Health and safety notes stay off the person record. Clubs are isolated from each other.',
  },
] as const;

export const featureTabs = [
  {
    id: 'roster',
    label: 'Roster',
    title: 'One roster for the squad.',
    body: 'The squad screen shows who is in, which lane they swim, and which coach is on that squad.',
    points: ['roster'] as const,
    image: '/marketing/roster.webp',
    avif: '/marketing/roster.avif',
    alt: 'Squad roster for the fictional Harbourside Swim Club',
    device: 'macbook' as const,
    tone: 'pool' as const,
    tilt: null,
    companion: null,
    float: null,
  },
  {
    id: 'training',
    label: 'Training',
    title: 'The session, and who is in it.',
    body: 'Each session shows the pool, the time, the lanes, and the coach. Families book a place from the phone.',
    points: ['sessions'] as const,
    image: '/marketing/sessions-1440.webp',
    avif: '/marketing/sessions-1440.avif',
    alt: 'Sessions list for the fictional Harbourside Swim Club',
    device: 'macbook' as const,
    tone: 'tide' as const,
    tilt: 'left' as const,
    companion: {
      image: '/marketing/pwa-sessions.webp',
      avif: '/marketing/pwa-sessions.avif',
      alt: 'Parent app session booking for a fictional Harbourside family',
      device: 'phone' as const,
    },
    float: null,
  },
  {
    id: 'meets',
    label: 'Meets',
    title: 'Times, personal bests, and the meet.',
    body: 'Results sit on the meet they came from, including a new personal best. Parents still open Fastlane for entries. There is no SNZ API sync yet.',
    points: ['times', 'meets'] as const,
    image: '/marketing/meets.webp',
    avif: '/marketing/meets.avif',
    alt: 'Meets screen for the fictional Harbourside Swim Club',
    device: 'macbook' as const,
    tone: 'sunset' as const,
    tilt: 'right' as const,
    companion: null,
    float: null,
  },
  {
    id: 'club',
    label: 'Club',
    title: 'The parent phone, and the invoices.',
    body: 'Families see the next session and club news on the phone. The club sees the invoices beside that.',
    points: ['comms'] as const,
    image: '/marketing/pwa-home.webp',
    avif: '/marketing/pwa-home.avif',
    alt: 'Parent app home for a fictional Harbourside family',
    device: 'phone' as const,
    tone: 'green' as const,
    tilt: null,
    companion: {
      image: '/marketing/finance.webp',
      avif: '/marketing/finance.avif',
      alt: 'Invoices for the fictional Harbourside Swim Club',
      device: 'tablet' as const,
    },
    float: null,
  },
] as const;

export const privacyPoints = [
  {
    title: 'Notes stay separate.',
    body: 'Health and safety notes are stored off the person record, with a short reader list. A guardian, or a swimmer aged 16 or over, authorises them. Swimmers under 18 do not get their own login.',
  },
  {
    title: 'Clubs never see each other.',
    body: 'Each club is its own tenant. One club’s roster, notes, and messages are not visible to another.',
  },
  {
    title: 'You can leave with your data.',
    body: 'Export your data any time. There is no lock-in.',
  },
] as const;

/** Founder’s own line, already published on the live site. Not a customer testimonial. */
export const founderQuote = {
  line: 'Built next to a real Kiwi club. Not in a startup deck.',
  body: 'I coach, and I have a swimmer at home. Nothing on the market fit a volunteer-run New Zealand club that moves quickly. So we built this poolside. Coaches on the deck, parents on the school run, the committee in one place.',
  by: 'David Gillespie',
} as const;

export const coachPage = {
  title: 'For the coach on the deck.',
  sub: 'A voice note, a parent email in the coach’s voice, and the week’s programme.',
} as const;

export const pricingIntro = {
  title: 'Flat pricing for the club.',
  sub: 'Not per swimmer. All prices include GST. Early adopter clubs get 50% off Solo for the first season.',
} as const;

export const pricingTiers: readonly PricingTier[] = [
  {
    id: 'solo',
    name: 'Solo',
    tagline: 'Single club, up to 100 swimmers',
    priceMonthly: 89,
    priceYearly: 1068,
    swimmers: 'Up to 100 swimmers',
    seats: '3 coach and admin seats',
    pools: '2 pools',
    teaser: ['Up to 100 swimmers', '3 coach and admin seats', 'Parent app and co-pilot'],
    features: [
      'Member roster, squads, and sessions',
      'Parent app with schedule, book-in, and news',
      'Co-pilot voice notes and parent email drafts',
      'CSV and spreadsheet import',
      'SNZ member IDs and Fastlane links',
      'Meets, manual results, and a basic site',
      'Transactional email for magic links and reminders',
    ],
    cta: 'Book a demo',
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Multi-squad club, up to 200 swimmers',
    priceMonthly: 149,
    priceYearly: 1788,
    swimmers: 'Up to 200 swimmers',
    seats: '10 coach and admin seats',
    pools: '4 pools',
    teaser: ['Up to 200 swimmers', '10 coach and admin seats', 'Splash, comms, and Xero'],
    features: [
      'Everything in Solo',
      'Splash parent chat and FAQ',
      'Multi-channel comms for email and Facebook',
      'Stripe term billing and Xero invoice sync',
      'Meet live push and performance narratives',
      'Unlimited public site for blog, events, and heroes',
      'AGM pack and treasurer reports',
    ],
    highlighted: true,
    cta: 'Book a demo',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'Large club or regional programme',
    priceMonthly: 249,
    priceYearly: 2988,
    swimmers: '200 or more swimmers, soft cap 400',
    seats: 'Unlimited seats',
    pools: 'Unlimited pools',
    teaser: ['200 or more swimmers', 'Unlimited seats', 'Your domain and priority support'],
    features: [
      'Everything in Pro',
      'Custom domain and white-label',
      'Complex multi-pool scheduling',
      'WhatsApp Business automation',
      'Priority support and onboarding',
      'API access and custom exports',
      'Advanced volunteer management',
    ],
    cta: 'Talk to us',
  },
];

export const comparisonRows: ReadonlyArray<{
  label: string;
  solo: string;
  pro: string;
  enterprise: string;
}> = [
  { label: 'Monthly price, GST included', solo: 'NZ$89', pro: 'NZ$149', enterprise: 'NZ$249' },
  { label: 'Yearly price, GST included', solo: 'NZ$1,068', pro: 'NZ$1,788', enterprise: 'NZ$2,988' },
  { label: 'Swimmers', solo: 'Up to 100', pro: 'Up to 200', enterprise: '200 or more, soft cap 400' },
  { label: 'Coach and admin seats', solo: '3', pro: '10', enterprise: 'Unlimited' },
  { label: 'Pools', solo: '2', pro: '4', enterprise: 'Unlimited' },
  { label: 'Roster, squads, and sessions', solo: 'Yes', pro: 'Yes', enterprise: 'Yes' },
  { label: 'Parent app', solo: 'Yes', pro: 'Yes', enterprise: 'Yes' },
  { label: 'Co-pilot voice notes and drafts', solo: 'Yes', pro: 'Yes', enterprise: 'Yes' },
  { label: 'CSV and spreadsheet import', solo: 'Yes', pro: 'Yes', enterprise: 'Yes' },
  { label: 'SNZ member IDs and Fastlane links', solo: 'Yes', pro: 'Yes', enterprise: 'Yes' },
  { label: 'Meets and manual results', solo: 'Yes', pro: 'Yes', enterprise: 'Yes' },
  { label: 'Public site', solo: 'Basic', pro: 'Unlimited', enterprise: 'Unlimited' },
  { label: 'Splash parent chat', solo: 'No', pro: 'Yes', enterprise: 'Yes' },
  { label: 'Email and Facebook comms', solo: 'No', pro: 'Yes', enterprise: 'Yes' },
  { label: 'Stripe billing and Xero invoices', solo: 'No', pro: 'Yes', enterprise: 'Yes' },
  { label: 'Meet live push and narratives', solo: 'No', pro: 'Yes', enterprise: 'Yes' },
  { label: 'AGM pack and treasurer reports', solo: 'No', pro: 'Yes', enterprise: 'Yes' },
  { label: 'Custom domain and white-label', solo: 'No', pro: 'No', enterprise: 'Yes' },
  { label: 'Multi-pool scheduling', solo: 'No', pro: 'No', enterprise: 'Yes' },
  { label: 'WhatsApp Business automation', solo: 'No', pro: 'No', enterprise: 'Yes' },
  { label: 'Priority support and onboarding', solo: 'No', pro: 'No', enterprise: 'Yes' },
  { label: 'API access and custom exports', solo: 'No', pro: 'No', enterprise: 'Yes' },
  { label: 'Advanced volunteer management', solo: 'No', pro: 'No', enterprise: 'Yes' },
];

export const pricingNotes = [
  'All prices include GST.',
  'Early adopter clubs get 50% off Solo for the first season.',
  'The pilot is in late 2026. We are building poolside with a reference club now. Clubs joining as early adopters, 2 to 5 clubs, lock in first-season pricing and help shape the product.',
] as const;

export const faqs = [
  {
    q: 'Do prices include GST?',
    a: 'Yes. Every published price includes GST.',
  },
  {
    q: 'Is the price per swimmer?',
    a: 'No. It is a flat price for the club.',
  },
  {
    q: 'What do early adopters get?',
    a: 'Early adopter clubs get 50% off Solo for the first season.',
  },
  {
    q: 'When can a club join?',
    a: 'The pilot is in late 2026. We are building poolside with a reference club now. Clubs joining as early adopters, 2 to 5 clubs, lock in first-season pricing and help shape the product.',
  },
] as const;

export const closing = {
  title: 'See it with your club.',
  sub: 'Book a 20 minute demo. The pilot opens in late 2026.',
} as const;

export const navLinks = [
  { to: '/features', label: 'Features', end: false },
  { to: '/coaches', label: 'Coaches', end: false },
  { to: '/pricing', label: 'Pricing', end: false },
  { to: '/story', label: 'Story', end: false },
] as const;

export const founderStory = {
  title: 'Built beside a real club',
  byline: 'David Gillespie, coach at Whangārei Academy of Swimming Club',
  paragraphs: [
    'David Gillespie coaches the Development Squad at Whangārei Academy of Swimming Club, and he has a swimmer in the club too. Most weeknights you\'ll find him at the Whangārei Aquatic Centre, either on deck or waiting in the car park.',
    'The club had a website with a timetable that worked. Everything else was harder. Squad lists sat in spreadsheets, and coaching cover got sorted in group chats. Working out who was coming on Thursday meant scrolling through emails.',
    'David looked for something to fix that and couldn\'t find anything built for a club like his. Fastlane handles entries and Swimming New Zealand handles results, but nothing covered the everyday stuff in between. So in May 2026 he started building it himself.',
    'That became Marshal. Coaches can record a quick voice note about a swimmer on their phone, and Marshal turns it into an email to the parent. The coach checks it, tweaks it if needed and sends it. Parents get one app for the timetable, bookings, invoices and club news. The committee works from one roster, and coaches can swap sessions without a flurry of messages.',
    'The club still makes its own calls on fees, lanes and who swims where. Marshal just takes the admin off their plate.',
    'It\'s been built with the club rather than for it, and most of what\'s in it started as a request from a coach, a parent or someone on the committee.',
    'That includes the club\'s public website, which runs straight from Marshal. The committee can post news, update the timetable or put up a page for a meet like Spookfest on 1 November in a few minutes, from the same place they run everything else. Nobody needs to know any code or anything about web design, and nobody has to wait for a web person to make the change. Because updates are that easy, the site stays current, and families and new swimmers always see what\'s actually happening at the club.',
  ],
} as const;

export function nzd(amount: number): string {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    maximumFractionDigits: 0,
  }).format(amount);
}
